import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class GetEmployeePHProfileParams {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetContentSearchPositionOptionsQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  company_ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetContentSearchCompanyOptionsQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];
}

export class GetEmployeePHProfileResponse {
  @IsString()
  @IsOptional()
  url?: string;
}

export class FilterSelectBadge {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['blue', 'velvet', 'red'])
  @IsString()
  @IsOptional()
  color?: 'blue' | 'velvet' | 'red';

  @IsIn(['left', 'bottom'])
  @IsString()
  @IsOptional()
  position?: 'left' | 'bottom';
}

export class FilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsIn(['item'])
  @IsString()
  @IsOptional()
  type?: 'item';

  @ValidateNested()
  @Type(() => FilterSelectBadge)
  @IsOptional()
  badge?: FilterSelectBadge;
}

export class FilterSelectOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => FilterSelectOption)
  @IsArray()
  @IsNotEmpty()
  options: FilterSelectOption[];
}

export class CommunityPostSearchFullDataUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  second_name: string;

  @IsString()
  @IsNotEmpty()
  middle_name: string;
}

export class CommunityPostSearchFullData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  community_id: string;

  @IsString()
  @IsNotEmpty()
  community_title: string;

  @IsString()
  @IsNotEmpty()
  community_slug: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  text?: string;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsUUID()
  @IsOptional()
  avatar_attachment_id?: string;

  @ValidateNested()
  @Type(() => CommunityPostSearchFullDataUser)
  @IsOptional()
  user?: CommunityPostSearchFullDataUser;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsIn(['community_post'])
  @IsString()
  @IsNotEmpty()
  type: 'community_post';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class CommunitySearchFullData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsString()
  @IsNotEmpty()
  slug: string;

  @IsUUID()
  @IsOptional()
  avatar_attachment_id?: string;

  @IsInt()
  @IsNotEmpty()
  members_count: number;

  @IsBoolean()
  @IsNotEmpty()
  is_member: boolean;

  @IsIn(['community'])
  @IsString()
  @IsNotEmpty()
  type: 'community';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export type AbsenceGroupNick = string;

export class AbsenceGroup {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn([
    'vacation',
    'maternity_leave',
    'student_leave',
    'business_trip',
    'sick_leave',
  ])
  @IsString()
  @IsNotEmpty()
  nick:
    | 'vacation'
    | 'maternity_leave'
    | 'student_leave'
    | 'business_trip'
    | 'sick_leave';
}

export class EmployeeAbsence {
  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @ValidateNested()
  @Type(() => AbsenceGroup)
  @IsNotEmpty()
  absence_group: AbsenceGroup;
}

export class EmployeeSearchFullData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  middle_name?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;

  @IsString()
  @IsOptional()
  unit_name?: string;

  @IsString()
  @IsOptional()
  position_name?: string;

  @IsString()
  @IsOptional()
  display_number?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  work_email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  personal_email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  phone?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  work_phone?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  work_mobile_phone?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  personal_mobile_phone?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  home_phone?: string;

  @IsBoolean()
  @IsNotEmpty()
  ph_profile: boolean;

  @ValidateNested({ each: true })
  @Type(() => EmployeeAbsence)
  @IsArray()
  @IsOptional()
  absences?: EmployeeAbsence[];

  @IsIn(['employee'])
  @IsString()
  @IsNotEmpty()
  type: 'employee';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class EventsSearchFullData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['ph_events'])
  @IsString()
  @IsNotEmpty()
  type: 'ph_events';

  @IsIn(['offline', 'online'])
  @IsString()
  @IsNotEmpty()
  format: 'offline' | 'online';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsDateString()
  @IsNotEmpty()
  since: string;

  @IsDateString()
  @IsNotEmpty()
  till: string;

  @IsString()
  @IsOptional()
  place?: string;

  @IsInt()
  @IsOptional()
  members_count?: number;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class FileStoragesSearchFullData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['ph_filestorage'])
  @IsString()
  @IsNotEmpty()
  type: 'ph_filestorage';

  @IsIn(['file', 'folder'])
  @IsString()
  @IsNotEmpty()
  kind: 'file' | 'folder';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class FilesSearchFullData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['ph_files'])
  @IsString()
  @IsNotEmpty()
  type: 'ph_files';

  @IsIn(['file', 'folder'])
  @IsString()
  @IsNotEmpty()
  kind: 'file' | 'folder';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class ListsSearchFullData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['ph_lists'])
  @IsString()
  @IsNotEmpty()
  type: 'ph_lists';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class PagesSearchFullData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['ph_pages'])
  @IsString()
  @IsNotEmpty()
  type: 'ph_pages';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class SitesSearchFullData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['ph_sites'])
  @IsString()
  @IsNotEmpty()
  type: 'ph_sites';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class SurveySearchFullDataUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  second_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;
}

export class SurveySearchFullData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['survey'])
  @IsString()
  @IsNotEmpty()
  type: 'survey';

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsBoolean()
  @IsNotEmpty()
  completed: boolean;

  @IsBoolean()
  @IsNotEmpty()
  passed_by_actor: boolean;

  @ValidateNested()
  @Type(() => SurveySearchFullDataUser)
  @IsOptional()
  user?: SurveySearchFullDataUser;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsUUID()
  @IsOptional()
  cover_att_id?: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class CommentSearchFullDataUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  second_name: string;

  @IsString()
  @IsNotEmpty()
  middle_name: string;
}

export class CommentSearchFullDataAffiliation {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['news', 'community_post'])
  @IsString()
  @IsNotEmpty()
  name: 'news' | 'community_post';

  @IsUUID()
  @IsOptional()
  community_id?: string;

  @IsString()
  @IsOptional()
  community_slug?: string;
}

export class CommentSearchFullData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['comment'])
  @IsString()
  @IsNotEmpty()
  type: 'comment';

  @ValidateNested()
  @Type(() => CommentSearchFullDataAffiliation)
  @IsNotEmpty()
  affiliation: CommentSearchFullDataAffiliation;

  @ValidateNested()
  @Type(() => CommentSearchFullDataUser)
  @IsNotEmpty()
  user: CommentSearchFullDataUser;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  target_name: string;

  @IsString()
  @IsNotEmpty()
  text: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class NewsSearchFullData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['news'])
  @IsString()
  @IsNotEmpty()
  type: 'news';

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsUUID()
  @IsOptional()
  cover_att_id?: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  found_words?: string[];

  @IsInt()
  @IsOptional()
  total_found_words?: number;
}

export class ContentSearchFullResponse {
  @IsArray()
  @IsNotEmpty()
  items: (
    | NewsSearchFullData
    | CommentSearchFullData
    | SurveySearchFullData
    | SitesSearchFullData
    | PagesSearchFullData
    | ListsSearchFullData
    | FilesSearchFullData
    | FileStoragesSearchFullData
    | EventsSearchFullData
    | EmployeeSearchFullData
    | CommunitySearchFullData
    | CommunityPostSearchFullData
  )[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class CommunityPostSearchDataUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  second_name: string;

  @IsString()
  @IsNotEmpty()
  middle_name: string;
}

export class CommunityPostSearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  community_id: string;

  @IsString()
  @IsNotEmpty()
  community_title: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @ValidateNested()
  @Type(() => CommunityPostSearchDataUser)
  @IsOptional()
  user?: CommunityPostSearchDataUser;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsString()
  @IsNotEmpty()
  community_slug: string;

  @IsUUID()
  @IsOptional()
  avatar_attachment_id?: string;
}

export class ContentSearchResponseCommunity_posts {
  @ValidateNested({ each: true })
  @Type(() => CommunityPostSearchData)
  @IsArray()
  @IsNotEmpty()
  items: CommunityPostSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class CommunitySearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsString()
  @IsNotEmpty()
  slug: string;

  @IsUUID()
  @IsOptional()
  avatar_attachment_id?: string;

  @IsInt()
  @IsNotEmpty()
  members_count: number;

  @IsBoolean()
  @IsNotEmpty()
  is_member: boolean;
}

export class ContentSearchResponseCommunities {
  @ValidateNested({ each: true })
  @Type(() => CommunitySearchData)
  @IsArray()
  @IsNotEmpty()
  items: CommunitySearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class EmployeeSearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  middle_name?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;

  @IsString()
  @IsOptional()
  unit_name?: string;

  @IsString()
  @IsOptional()
  position_name?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  phone?: string;

  @IsBoolean()
  @IsNotEmpty()
  ph_profile: boolean;

  @ValidateNested({ each: true })
  @Type(() => EmployeeAbsence)
  @IsArray()
  @IsOptional()
  absences?: EmployeeAbsence[];
}

export class ContentSearchResponseEmployees {
  @ValidateNested({ each: true })
  @Type(() => EmployeeSearchData)
  @IsArray()
  @IsNotEmpty()
  items: EmployeeSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class EventsSearchData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['offline', 'online'])
  @IsString()
  @IsNotEmpty()
  format: 'offline' | 'online';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;

  @IsDateString()
  @IsNotEmpty()
  since: string;

  @IsDateString()
  @IsNotEmpty()
  till: string;

  @IsString()
  @IsOptional()
  place?: string;
}

export class ContentSearchResponseEvents {
  @ValidateNested({ each: true })
  @Type(() => EventsSearchData)
  @IsArray()
  @IsNotEmpty()
  items: EventsSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class FileStoragesSearchData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['file', 'folder'])
  @IsString()
  @IsNotEmpty()
  kind: 'file' | 'folder';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;
}

export class ContentSearchResponseFilestorages {
  @ValidateNested({ each: true })
  @Type(() => FileStoragesSearchData)
  @IsArray()
  @IsNotEmpty()
  items: FileStoragesSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class SitesAllSearchData {
  @IsString()
  @IsNotEmpty()
  id: string;

  @IsIn(['sites', 'pages', 'lists', 'files'])
  @IsString()
  @IsNotEmpty()
  group: 'sites' | 'pages' | 'lists' | 'files';

  @IsIn(['file', 'folder'])
  @IsString()
  @IsOptional()
  kind?: 'file' | 'folder';

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  link: string;
}

export class ContentSearchResponseSites_all {
  @ValidateNested({ each: true })
  @Type(() => SitesAllSearchData)
  @IsArray()
  @IsNotEmpty()
  items: SitesAllSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class SurveySearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsBoolean()
  @IsNotEmpty()
  completed: boolean;

  @IsBoolean()
  @IsNotEmpty()
  passed_by_actor: boolean;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsUUID()
  @IsOptional()
  cover_att_id?: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;
}

export class ContentSearchResponseSurveys {
  @ValidateNested({ each: true })
  @Type(() => SurveySearchData)
  @IsArray()
  @IsNotEmpty()
  items: SurveySearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class CommentSearchDataUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  second_name: string;

  @IsString()
  @IsNotEmpty()
  middle_name: string;
}

export class CommentSearchDataAffiliation {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['news', 'community_post'])
  @IsString()
  @IsNotEmpty()
  name: 'news' | 'community_post';

  @IsUUID()
  @IsOptional()
  community_id?: string;

  @IsString()
  @IsOptional()
  community_slug?: string;
}

export class CommentSearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => CommentSearchDataAffiliation)
  @IsNotEmpty()
  affiliation: CommentSearchDataAffiliation;

  @ValidateNested()
  @Type(() => CommentSearchDataUser)
  @IsNotEmpty()
  user: CommentSearchDataUser;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  target_name: string;

  @IsString()
  @IsNotEmpty()
  text: string;
}

export class ContentSearchResponseComments {
  @ValidateNested({ each: true })
  @Type(() => CommentSearchData)
  @IsArray()
  @IsNotEmpty()
  items: CommentSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class NewsSearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsUUID()
  @IsOptional()
  cover_att_id?: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;
}

export class ContentSearchResponseNews {
  @ValidateNested({ each: true })
  @Type(() => NewsSearchData)
  @IsArray()
  @IsNotEmpty()
  items: NewsSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class ContentSearchResponse {
  @ValidateNested()
  @Type(() => ContentSearchResponseNews)
  @IsNotEmpty()
  news: ContentSearchResponseNews;

  @ValidateNested()
  @Type(() => ContentSearchResponseComments)
  @IsNotEmpty()
  comments: ContentSearchResponseComments;

  @ValidateNested()
  @Type(() => ContentSearchResponseSurveys)
  @IsNotEmpty()
  surveys: ContentSearchResponseSurveys;

  @ValidateNested()
  @Type(() => ContentSearchResponseSites_all)
  @IsNotEmpty()
  sites_all: ContentSearchResponseSites_all;

  @ValidateNested()
  @Type(() => ContentSearchResponseFilestorages)
  @IsNotEmpty()
  filestorages: ContentSearchResponseFilestorages;

  @ValidateNested()
  @Type(() => ContentSearchResponseEvents)
  @IsNotEmpty()
  events: ContentSearchResponseEvents;

  @ValidateNested()
  @Type(() => ContentSearchResponseEmployees)
  @IsNotEmpty()
  employees: ContentSearchResponseEmployees;

  @ValidateNested()
  @Type(() => ContentSearchResponseCommunities)
  @IsNotEmpty()
  communities: ContentSearchResponseCommunities;

  @ValidateNested()
  @Type(() => ContentSearchResponseCommunity_posts)
  @IsNotEmpty()
  community_posts: ContentSearchResponseCommunity_posts;
}

export type ContentSearchFullGroup = string;

export type ContentSearchGroup = string;

export class ContentSearchGroupsResponse {
  @IsIn(
    [
      'news',
      'comments',
      'surveys',
      'sites_all',
      'filestorages',
      'events',
      'employees',
      'communities',
      'community_posts',
    ],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  search_groups?: (
    | 'news'
    | 'comments'
    | 'surveys'
    | 'sites_all'
    | 'filestorages'
    | 'events'
    | 'employees'
    | 'communities'
    | 'community_posts'
  )[];

  @IsIn(
    [
      'news',
      'comments',
      'comments_news',
      'comments_community_posts',
      'surveys',
      'sites_all',
      'sites',
      'pages',
      'lists',
      'files',
      'filestorages',
      'events',
      'employees',
      'communities',
      'community_posts',
    ],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  full_search_groups?: (
    | 'news'
    | 'comments'
    | 'comments_news'
    | 'comments_community_posts'
    | 'surveys'
    | 'sites_all'
    | 'sites'
    | 'pages'
    | 'lists'
    | 'files'
    | 'filestorages'
    | 'events'
    | 'employees'
    | 'communities'
    | 'community_posts'
  )[];
}

export type ContentSearchEmployeeField = string;

export class ContentSearchFullRequest {
  @IsString()
  @IsOptional()
  query?: string;

  @IsIn([
    'news',
    'comments',
    'comments_news',
    'comments_community_posts',
    'surveys',
    'sites_all',
    'sites',
    'pages',
    'lists',
    'files',
    'filestorages',
    'events',
    'employees',
    'communities',
    'community_posts',
  ])
  @IsString()
  @IsOptional()
  group?:
    | 'news'
    | 'comments'
    | 'comments_news'
    | 'comments_community_posts'
    | 'surveys'
    | 'sites_all'
    | 'sites'
    | 'pages'
    | 'lists'
    | 'files'
    | 'filestorages'
    | 'events'
    | 'employees'
    | 'communities'
    | 'community_posts';

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @IsIn(
    ['name', 'display_number', 'phone', 'email', 'unit', 'position', 'company'],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  search_fields?: (
    | 'name'
    | 'display_number'
    | 'phone'
    | 'email'
    | 'unit'
    | 'position'
    | 'company'
  )[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  company_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  position_names?: string[];

  @IsInt()
  @IsOptional()
  birthday_day?: number;

  @IsInt()
  @IsOptional()
  birthday_month?: number;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class ContentSearchRequest {
  @IsString()
  @IsOptional()
  query?: string;

  @IsIn([
    'news',
    'comments',
    'surveys',
    'sites_all',
    'filestorages',
    'events',
    'employees',
    'communities',
    'community_posts',
  ])
  @IsString()
  @IsOptional()
  group?:
    | 'news'
    | 'comments'
    | 'surveys'
    | 'sites_all'
    | 'filestorages'
    | 'events'
    | 'employees'
    | 'communities'
    | 'community_posts';
}

@injectable()
export class SearchService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getEmployeePHProfile({
    params,
  }: {
    params: GetEmployeePHProfileParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEmployeePHProfileParams),
    ]);

    let requestPath = '/content/search/ph_profile/{employee_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEmployeePHProfileParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEmployeePHProfileResponse,
    );

    return response;
  }

  async getContentSearchPositionOptions({
    query = {},
  }: {
    query?: GetContentSearchPositionOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetContentSearchPositionOptionsQuery,
      ),
    ]);

    let requestPath = '/content/search/filters/position_options';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      FilterSelectOptionsResponse,
    );

    return response;
  }

  async getContentSearchCompanyOptions({
    query = {},
  }: {
    query?: GetContentSearchCompanyOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetContentSearchCompanyOptionsQuery,
      ),
    ]);

    let requestPath = '/content/search/filters/company_options';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      FilterSelectOptionsResponse,
    );

    return response;
  }

  async getContentSearchAccountOptions() {
    let requestPath = '/content/search/filters/account_options';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      FilterSelectOptionsResponse,
    );

    return response;
  }

  async contentSearchFull({
    body = {},
  }: {
    body?: ContentSearchFullRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, ContentSearchFullRequest),
    ]);

    let requestPath = '/content/search_full';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ContentSearchFullResponse,
    );

    return response;
  }

  async contentSearch({
    body = {},
  }: {
    body?: ContentSearchRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(body, ContentSearchRequest),
    ]);

    let requestPath = '/content/search';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ContentSearchResponse,
    );

    return response;
  }

  async getContentSearchGroups() {
    let requestPath = '/content/search/group';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ContentSearchGroupsResponse,
    );

    return response;
  }
}
